import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../css/Dashboard.css";
import StateVsPunchedEmployees from "../components/StateVsPunchedEmployees";
import StateVsMonthlyPOB from "../components/StateVsMonthlyPOB";
import "../css/RealTimeBarChart.css";
import AttendanceChart from "../components/AttendanceChart";
import { BASE_URL } from "../config";
import { formatDate } from "../utils/formatDate";
import CordinatorChart from "../Admin/coordinator/CordinatorChart";
import axios from "../axiosConfig";

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const dashboardAccess =
    JSON.parse(localStorage.getItem("dashboard_access")) || {};

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BASE_URL}/api/dashboard`);
        setDashboardData(res.data.data || null);
      } catch (err) {
        console.error("Error fetching dashboard:", err);
        setDashboardData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  const birthdays = dashboardData?.birthdays || [];
  const punchedData = dashboardData?.state_vs_punched_employees || [];
  const pobData = dashboardData?.state_vs_monthly_pob || [];
  const attendanceSummary = dashboardData?.attendance_summary || null;
  const expenseSummary = dashboardData?.expense_summary || null;
  const isAdmin = dashboardAccess?.dashboard_admin === true;
  const isCoordinator = dashboardAccess?.dashboard_coordinator === true;
  const isExpense = dashboardAccess?.dashboard_expense === true;

  return (
    <div className="dashboard">
      <table className="table1" cellPadding="5" cellSpacing="0" width="100%">
        <tbody>
          <tr>
            <td valign="top" style={{ border: "1px solid black" }}>
              <fieldset>
                <legend className="normal">
                  {isExpense ? "Expense Chart" : "Attendance Chart"}
                </legend>
                <AttendanceChart
                  attendanceSummary={attendanceSummary}
                  expenseSummary={expenseSummary}
                  isExpense={isExpense}
                />
              </fieldset>
            </td>
          </tr>
        </tbody>
      </table>

      <fieldset className="hr-coordinator-panel">
        <legend className="legend">BIRTHDAY PANEL</legend>

        {/* Birthday Table */}
        <div className="w-full mb-6">
          <div className="bg-white rounded-2xl shadow-lg shadow-blue-100 overflow-hidden border border-blue-100">
            {/* Scrollable Table */}
            <div className="max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-blue-300 scrollbar-track-blue-50">
              <table className="w-full text-sm border-collapse">
                {/* Sticky Header */}
                <thead className="sticky top-0 z-10">
                  <tr className="bg-blue-900">
                    {[
                      "S.No",
                      "Employee",
                      "Date of Birth",
                      "Division",
                      "Designation",
                      "State",
                      "City",
                      "Phone",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-blue-100 font-semibold text-xs uppercase tracking-wider text-center whitespace-nowrap border-b-2 border-blue-600"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan="8"
                        className="py-10 text-center text-gray-400 text-sm"
                      >
                        <div className="flex items-center justify-center gap-2">
                          <svg
                            className="animate-spin w-4 h-4 text-blue-500"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                          Loading...
                        </div>
                      </td>
                    </tr>
                  ) : birthdays.length > 0 ? (
                    birthdays.map((emp, index) => (
                      <tr
                        key={`${emp.employee}-${index}`}
                        className="border-b border-blue-50 even:bg-blue-50/40 hover:bg-indigo-50 transition-colors duration-150"
                      >
                        {/* S.No */}
                        <td className="px-4 py-2.5 text-center">
                          <span className="font-bold text-indigo-500 text-xs">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                        </td>

                        {/* Employee Name */}
                        <td className="px-4 py-2.5 text-center">
                          <span className="font-semibold text-blue-800">
                            {emp.first_name} {emp.last_name}
                          </span>
                        </td>

                        {/* DOB */}
                        <td className="px-4 py-2.5 text-center">
                          <span className="inline-block bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-0.5 text-xs font-semibold">
                            {formatDate(emp.dob)}
                          </span>
                        </td>

                        {/* Division */}
                        <td className="px-4 py-2.5 text-center">
                          <span className="inline-block bg-blue-50 text-blue-700 border border-blue-200 rounded-md px-2.5 py-0.5 text-xs font-medium">
                            {emp.division}
                          </span>
                        </td>

                        {/* Designation */}
                        <td className="px-4 py-2.5 text-center text-gray-600 text-xs">
                          {emp.designation}
                        </td>

                        {/* State */}
                        <td className="px-4 py-2.5 text-center text-gray-700 text-xs">
                          {emp.state || (
                            <span className="text-gray-300">N/A</span>
                          )}
                        </td>

                        {/* City */}
                        <td className="px-4 py-2.5 text-center text-gray-700 text-xs">
                          {emp.city || (
                            <span className="text-gray-300">N/A</span>
                          )}
                        </td>

                        {/* Phone */}
                        <td className="px-4 py-2.5 text-center">
                          {emp.phone ? (
                            <span className="inline-block bg-purple-50 text-purple-700 border border-purple-200 rounded-md px-2.5 py-0.5 text-xs">
                              {emp.phone}
                            </span>
                          ) : (
                            <span className="text-gray-300 text-xs">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="8"
                        className="py-10 text-center text-gray-400 text-sm"
                      >
                        🎈 No birthdays found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {!loading && isAdmin && (
          <>
            <table
              className="table1"
              cellPadding="5"
              cellSpacing="0"
              width="100%"
            >
              <tbody>
                <tr>
                  <td valign="top" style={{ border: "1px solid black" }}>
                    <fieldset>
                      <legend className="normal">
                        State Vs Punched Employee
                      </legend>
                      <StateVsPunchedEmployees data={punchedData} />
                    </fieldset>
                  </td>
                </tr>
              </tbody>
            </table>

            <table
              className="table1"
              cellPadding="5"
              cellSpacing="0"
              width="100%"
            >
              <tbody>
                <tr>
                  <td valign="top" style={{ border: "1px solid black" }}>
                    <fieldset>
                      <legend className="normal">State Vs Monthly POB</legend>
                      <StateVsMonthlyPOB data={pobData} />
                    </fieldset>
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        )}

        {!loading &&
          isCoordinator &&
          !(
            dashboardAccess.dashboard_coordinator &&
            dashboardAccess.dashboard_expense
          ) && (
            <table
              className="table1"
              cellPadding="5"
              cellSpacing="0"
              width="100%"
            >
              <tbody>
                <tr>
                  <td valign="top" style={{ border: "1px solid black" }}>
                    <CordinatorChart
                      pobData={dashboardData?.state_vs_pob_fy}
                      sobData={dashboardData?.state_vs_sob_fy}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          )}
      </fieldset>
    </div>
  );
};

export default Dashboard;
